// src/pages/ComposeMessage.js
// Compose a new message to a client. Creates a conversation + first message.
// URL params: ?to={clientEmail}&name={clientName} (pre-fill from ClientProfile)

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X, Send, Search, ArrowLeft } from 'lucide-react';
import { auth, db } from '../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const REGION = 'us-west1';

const getInitials = (name) =>
  (name || '?').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

// ---------------------------------------------------------------------------
// ComposeMessage
// ---------------------------------------------------------------------------
const ComposeMessage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const preEmail = searchParams.get('to') || '';
  const preName = searchParams.get('name') || '';

  const [practitionerId, setPractitionerId] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  const [recipientQuery, setRecipientQuery] = useState(preName || preEmail);
  const [selectedClient, setSelectedClient] = useState(
    preEmail ? { email: preEmail, displayName: preName || preEmail, id: null } : null
  );
  const [showDropdown, setShowDropdown] = useState(false);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const dropdownRef = useRef(null);
  const recipientInputRef = useRef(null);

  // Auth — safe pattern
  useEffect(() => {
    if (!auth) { setAuthLoading(false); return; }
    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setAuthLoading(false); } }, 3000);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!done) {
        done = true;
        clearTimeout(timeout);
        if (user) setPractitionerId(user.uid);
        setAuthLoading(false);
      }
    });
    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  // Load clients from Firestore once we have practitionerId
  useEffect(() => {
    if (!practitionerId || !db) return;
    setClientsLoading(true);
    const ref = collection(db, 'practitioners', practitionerId, 'clients');
    const q = query(ref, orderBy('displayName', 'asc'));
    getDocs(q)
      .then((snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setClients(list);
      })
      .catch((err) => console.error('ComposeMessage: client load error:', err))
      .finally(() => setClientsLoading(false));
  }, [practitionerId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          e.target !== recipientInputRef.current) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredClients = recipientQuery.length > 0
    ? clients.filter((c) => {
        const q = recipientQuery.toLowerCase();
        return (
          (c.displayName || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q)
        );
      }).slice(0, 8)
    : clients.slice(0, 8);

  const handleRecipientChange = (value) => {
    setRecipientQuery(value);
    setSelectedClient(null);
    setShowDropdown(true);
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setRecipientQuery(client.displayName || client.email);
    setShowDropdown(false);
  };

  const handleClearRecipient = () => {
    setSelectedClient(null);
    setRecipientQuery('');
    recipientInputRef.current?.focus();
    setShowDropdown(true);
  };

  const handleSend = async () => {
    if (!selectedClient || !subject.trim() || !message.trim()) return;
    if (!practitionerId) { setSendError('You must be signed in.'); return; }
    setSending(true);
    setSendError('');

    try {
      // Create conversation + first message via Cloud Function
      const functions = getFunctions(undefined, REGION);
      const fn = httpsCallable(functions, 'startConversation');
      const result = await fn({
        practitionerId,
        clientEmail: selectedClient.email,
        clientName: selectedClient.displayName || selectedClient.email,
        clientId: selectedClient.id || null,
        subject: subject.trim(),
        content: message.trim(),
      });

      const { conversationId } = result.data;
      navigate(`/messages?conv=${conversationId}`);
    } catch (err) {
      console.error('ComposeMessage: send failed:', err);
      setSendError(err.message || 'Failed to send message. Please try again.');
      setSending(false);
    }
  };

  const handleCancel = () => {
    if (subject || message) {
      if (window.confirm('Discard this message?')) navigate('/messages');
    } else {
      navigate('/messages');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back link */}
        <span
          role="button"
          tabIndex={0}
          onClick={() => navigate('/messages')}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate('/messages'); }}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-purple-600 transition-colors cursor-pointer mb-5 w-fit"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Messages
        </span>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">New Message</h2>
            <button onClick={handleCancel} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Recipient */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                To <span className="text-red-500">*</span>
              </label>

              {selectedClient ? (
                <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div
                    className="w-9 h-9 rounded-full text-white flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #6366f1)' }}
                  >
                    {getInitials(selectedClient.displayName || selectedClient.email)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{selectedClient.displayName || selectedClient.email}</p>
                    <p className="text-xs text-gray-500 truncate">{selectedClient.email}</p>
                  </div>
                  <button onClick={handleClearRecipient} className="p-1 hover:bg-purple-100 rounded-full transition-colors">
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      ref={recipientInputRef}
                      type="text"
                      value={recipientQuery}
                      onChange={(e) => handleRecipientChange(e.target.value)}
                      onFocus={() => setShowDropdown(true)}
                      placeholder="Search clients by name or email..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      autoComplete="off"
                    />
                  </div>
                  {showDropdown && (
                    <div
                      ref={dropdownRef}
                      className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-56 overflow-y-auto"
                    >
                      {clientsLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600" />
                        </div>
                      ) : filteredClients.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          {recipientQuery ? 'No clients found' : 'No clients yet — they appear after booking'}
                        </div>
                      ) : (
                        filteredClients.map((client) => (
                          <button
                            key={client.id || client.email}
                            onClick={() => handleSelectClient(client)}
                            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                          >
                            <div
                              className="w-9 h-9 rounded-full text-white flex items-center justify-center text-xs font-bold flex-shrink-0"
                              style={{ background: 'linear-gradient(135deg, #7c3aed, #6366f1)' }}
                            >
                              {getInitials(client.displayName || client.email)}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{client.displayName || client.email}</p>
                              <p className="text-xs text-gray-500 truncate">{client.email}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="What's this message about?"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                maxLength={120}
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message here..."
                rows={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{message.length} characters</p>
            </div>

            {sendError && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                <p className="text-sm text-red-600">{sendError}</p>
                <button onClick={() => setSendError('')}><X className="w-4 h-4 text-red-400" /></button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
            <button
              onClick={handleCancel}
              className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={!selectedClient || !subject.trim() || !message.trim() || sending}
              className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send Message
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComposeMessage;
